import type { Callout, Capture, Demo, DemoBundle, DemoSummary, FocusBoundary, Lead, Hotspot, MediaType, Product, PublishIssue, Redaction, TextCard } from "../shared/types";
import { REPLAY_VERSION } from "../shared/replayScript.generated";

export class ApiError extends Error {
  constructor(public status: number, message: string, public issues?: PublishIssue[]) {
    super(message);
  }
}

/**
 * Supplies the signed-in admin's session token (Clerk), sent as `Authorization: Bearer`. `fresh` skips the cached
 * token. Without a getter (the public player, local development sign-in) requests rely on cookies alone.
 */
export type AuthTokenGetter = (fresh: boolean) => Promise<string | null>;
let authToken: AuthTokenGetter | null = null;
export function setAuthTokenGetter(getter: AuthTokenGetter | null) { authToken = getter; }

/** Sends a request with the admin's credentials. A token that expired in flight is refreshed and the request retried once. */
async function send(method: string, path: string, body?: unknown): Promise<{ res: Response; hadToken: boolean }> {
  const headers: Record<string, string> = { "X-Requested-With": "demodive-admin" };
  let payload: BodyInit | undefined;
  if (body instanceof FormData) payload = body;
  else if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }
  const attempt = async (fresh: boolean) => {
    const token = authToken ? await authToken(fresh).catch(() => null) : null;
    const res = await fetch(`/api${path}`, { method, body: payload, credentials: "same-origin", headers: token ? { ...headers, Authorization: `Bearer ${token}` } : headers });
    return { res, hadToken: !!token };
  };
  const first = await attempt(false);
  if (first.res.status !== 401 || !first.hadToken) return first;
  await first.res.body?.cancel();
  return attempt(true);
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const { res, hadToken } = await send(method, path, body);
  const text = await res.text();
  let data: any = null;
  try { data = text ? JSON.parse(text) : null; } catch { /* non-JSON */ }
  if (!res.ok) {
    // Not signed in: go and sign in. A session the server refused even after a fresh token is different
    // (wrong keys, revoked): sending that person to the sign-in page would only bounce them straight back.
    if (res.status === 401 && !hadToken && path.startsWith("/admin") && !location.pathname.startsWith("/admin/login")) {
      location.href = `/admin/login?redirect_url=${encodeURIComponent(location.pathname + location.search)}`;
    }
    throw new ApiError(res.status, data?.error ?? `Request failed (${res.status})`, data?.issues);
  }
  return data as T;
}

/** Downloads an admin file (CSV, JSON export) with the admin's credentials, under the name the server gives it. */
async function download(path: string, fallbackName: string) {
  const { res } = await send("GET", path);
  if (!res.ok) {
    let message = `Download failed (${res.status})`;
    try { message = (await res.json()).error ?? message; } catch { /* non-JSON */ }
    throw new ApiError(res.status, message);
  }
  const name = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "")?.[1] ?? fallbackName;
  const url = URL.createObjectURL(await res.blob());
  const a = Object.assign(document.createElement("a"), { href: url, download: name });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** What /api/auth/me says about the person using the admin. */
export interface AuthStatus {
  mode: "clerk" | "local" | "unconfigured";
  /** Has a valid session (Clerk) or sign-in cookie (local). */
  authenticated: boolean;
  /** May use this workspace. */
  allowed: boolean;
  email?: string;
  /** Why not: signed_out, invalid_session, missing_claims, email_unverified, no_admins, not_allowed, auth_unconfigured. */
  code?: string;
  message?: string;
  detail?: string;
}

export type ProductWithDemos = Product & { demos: DemoSummary[] };

export interface SurveyConfig {
  surveyEnabled: boolean;
  surveyIntro: string;
  minOutcomeScore?: number;
  questions: { id: string; prompt: string; answerType: string; required: boolean; options: { id: string; label: string; attached_tags?: string[] }[] }[];
}

export const surveyConfigFrom = (b: DemoBundle): SurveyConfig => ({
  surveyEnabled: b.demo.surveyEnabled,
  surveyIntro: b.demo.surveyIntro,
  minOutcomeScore: b.demo.minOutcomeScore,
  questions: b.questions.map((q) => ({
    id: q.id, prompt: q.prompt, answerType: q.answerType, required: q.required,
    options: q.options.map((o) => ({ id: o.id, label: o.label, attached_tags: o.attached_tags ?? [] })),
  })),
});

export const imageUrl = (key: string) => `/api/images/${key}`;
/**
 * Where a capture's HTML replays. base = the bundle's captureBase ("" = this origin). `scrollable` is the
 * player's variant, which runs REPLAY_SCRIPT: its version is in the URL because the replay is cached as
 * immutable, and an old copy of the script would place hotspots and callouts differently from the Studio.
 */
export const captureUrl = (key: string, base = "", scrollable = false) =>
  `${base}/api/captures/${key.replace(/^cap\//, "")}${scrollable ? `?scroll=1&v=${REPLAY_VERSION}` : ""}`;

export const api = {
  me: () => request<AuthStatus>("GET", "/auth/me"),
  /** Local development only (see server/auth.ts). */
  login: (password: string) => request<{ ok: true }>("POST", "/auth/login", { password }),
  logout: () => request<{ ok: true }>("POST", "/auth/logout"),
  /** Makes the API fail on purpose, to check that its error reports arrive in monitoring. */
  monitoringTest: () => request("POST", "/admin/monitoring/test"),
  downloadExport: () => download("/admin/export", "demodive-export.json"),

  products: () => request<ProductWithDemos[]>("GET", "/admin/products"),
  product: (id: string) => request<ProductWithDemos>("GET", `/admin/products/${id}`),
  createProduct: (p: { name: string; slug: string; description: string; brandColor: string; logoKey: string | null }) =>
    request<Product>("POST", "/admin/products", p),
  updateProduct: (id: string, p: Partial<Product>) => request<Product>("PATCH", `/admin/products/${id}`, p),
  deleteProduct: (id: string) => request("DELETE", `/admin/products/${id}`),

  createDemo: (productId: string, d: { title: string; slug: string }) =>
    request<{ id: string }>("POST", `/admin/products/${productId}/demos`, d),
  demo: (id: string) => request<DemoBundle>("GET", `/admin/demos/${id}`),
  updateDemo: (id: string, d: Partial<Pick<Demo, "title" | "slug" | "ctaLabel" | "ctaUrl" | "transition" | "leadGate" | "leadGateStep" | "leadAskName" | "leadAskCompany" | "leadSkippable" | "leadTitle" | "leadWebhookUrl">>) =>
    request<DemoBundle>("PATCH", `/admin/demos/${id}`, d),
  deleteDemo: (id: string) => request("DELETE", `/admin/demos/${id}`),
  duplicateDemo: (id: string) => request<DemoBundle>("POST", `/admin/demos/${id}/duplicate`),
  saveSurvey: (id: string, cfg: SurveyConfig) => request<DemoBundle>("PUT", `/admin/demos/${id}/survey`, cfg),
  publish: (id: string) => request<DemoBundle>("POST", `/admin/demos/${id}/publish`),
  unpublish: (id: string) => request<DemoBundle>("POST", `/admin/demos/${id}/unpublish`),

  /** A new screen at the end of a path (outcomeId; null = the default path), or right `after` one of its screens. */
  addStep: (demoId: string, s: {
    outcomeId?: string | null;
    after?: string | null;
    imageKey?: string;
    imageWidth?: number;
    imageHeight?: number;
    mediaType?: MediaType;
    captureKey?: string | null;
    title?: string;
    subtitle?: string;
    screenSourceId?: string;
    textCard?: TextCard | null;
    chapterTitle?: string | null;
    redactions?: Redaction[];
    focus?: FocusBoundary | null;
  }) => request<DemoBundle>("POST", `/admin/demos/${demoId}/steps`, s),
  /** A path's new order (pathId null = the default path): exactly its own steps, reordered. */
  setPathSteps: (demoId: string, pathId: string | null, stepIds: string[]) =>
    request<DemoBundle>("PUT", `/admin/demos/${demoId}/paths/${pathId ?? "default"}/steps`, { stepIds }),
  /** Copies other paths' steps onto this path, at the end: the same media, steps of its own to edit. */
  copyToPath: (demoId: string, pathId: string | null, stepIds: string[]) =>
    request<{ added: string[]; bundle: DemoBundle }>("POST", `/admin/demos/${demoId}/paths/${pathId ?? "default"}/copy`, { stepIds }),
  /** A new path, starting with copies of the default path's steps. */
  addPath: (demoId: string, p: { name: string; templateKey?: string | null }) =>
    request<{ id: string; bundle: DemoBundle }>("POST", `/admin/demos/${demoId}/paths`, p),
  updatePath: (demoId: string, pathId: string, p: { name?: string; description?: string; tags?: string[] }) =>
    request<DemoBundle>("PATCH", `/admin/demos/${demoId}/paths/${pathId}`, p),
  deletePath: (demoId: string, pathId: string) => request<DemoBundle>("DELETE", `/admin/demos/${demoId}/paths/${pathId}`),
  /** The paths' order: a tie between paths matching a viewer's tags equally goes to the one higher up. */
  reorderPaths: (demoId: string, pathIds: string[]) => request<DemoBundle>("PUT", `/admin/demos/${demoId}/paths/order`, { pathIds }),
  updateStep: (id: string, s: Partial<{
    caption: string;
    autoAdvanceMs: number | null;
    callout: Callout | null;
    focus: FocusBoundary | null;
    imageKey: string;
    imageWidth: number;
    imageHeight: number;
    mediaType: MediaType;
    captureKey: string | null;
    title: string;
    subtitle: string;
    screenSourceId: string;
    textCard: TextCard | null;
    chapterTitle: string | null;
    redactions: Redaction[]; audioKey: string | null; transcript: any | null; audioReviewed: boolean;
  }>) => request<DemoBundle>("PATCH", `/admin/steps/${id}`, s),
  deleteStep: (id: string) => request<DemoBundle>("DELETE", `/admin/steps/${id}`),
  /** A copy of a step on a path (null = the default path), right after the original when it is on that path. */
  cloneStep: (id: string, outcomeId: string | null) =>
    request<DemoBundle>("POST", `/admin/steps/${id}/clone`, { outcomeId }),
  saveHotspots: (stepId: string, hotspots: Hotspot[]) => request<DemoBundle>("PUT", `/admin/steps/${stepId}/hotspots`, { hotspots }),

  leads: (demoId: string, days: number) => request<LeadRow[]>("GET", `/admin/demos/${demoId}/leads?days=${days}`),
  downloadLeadsCsv: (demoId: string, days: number) => download(`/admin/demos/${demoId}/leads?days=${days}&format=csv`, "leads.csv"),
  captures: () => request<Capture[]>("GET", "/admin/captures"),
  captureSetup: () => request<{ token: string; serverUrl: string; captureOrigin: string }>("GET", "/admin/capture-setup"),
  renameCapture: (id: string, title: string) => request<Capture>("PATCH", `/admin/captures/${id}`, { title }),
  deleteCapture: (id: string) => request("DELETE", `/admin/captures/${id}`),
  captureSource: async (key: string) => {
    const { res } = await send("GET", `/admin/capture-source/${key.replace(/^cap\//, "")}`);
    if (!res.ok) throw new ApiError(res.status, "Couldn't load the captured page.");
    return res.text();
  },
  /** Saves the edited page and, when given, the screen's hotspots and focal boundary with it in one request (they are tied to the page's scroll position and anchors). */
  saveStepCapture: (stepId: string, html: string, hotspots?: Hotspot[], focus?: FocusBoundary | null) =>
    request<DemoBundle>("PUT", `/admin/steps/${stepId}/capture`, {
      html,
      ...(hotspots ? { hotspots } : {}),
      ...(focus !== undefined ? { focus } : {}),
    }),
  analytics: (demoId: string, days: number) => request<any>("GET", `/admin/demos/${demoId}/analytics?days=${days}`),

  uploadImage: (file: File, width: number, height: number, mediaType: MediaType = "image", durationMs?: number) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("width", String(width));
    fd.append("height", String(height));
    fd.append("mediaType", mediaType);
    if (durationMs !== undefined) fd.append("durationMs", String(durationMs));
    return request<{ key: string; width: number; height: number; mediaType: MediaType; url: string }>("POST", "/admin/images", fd);
  },
  /** Uploads a step's voiceover; the server stores it, transcribes it and returns the updated bundle. */
  uploadStepAudio: (stepId: string, audio: Blob) => {
    const fd = new FormData();
    fd.append("file", audio, "voiceover.webm");
    return request<DemoBundle>("POST", `/admin/steps/${stepId}/audio`, fd);
  },

  publicDemo: (productSlug: string, demoSlug: string) =>
    request<DemoBundle>("GET", `/public/demos/${encodeURIComponent(productSlug)}/${encodeURIComponent(demoSlug)}`),
  startView: (v: { demoId: string; outcomeId: string | null; surveyAnswers: Record<string, unknown>; source: string }) =>
    request<{ viewId: string }>("POST", "/public/views", v),
  trackView: (id: string, p: { lastStepIndex: number; lastStepId: string | null; completed: boolean }) =>
    request("PATCH", `/public/views/${id}`, p),
  submitLead: (l: { demoId: string; viewId: string | null; email: string; name: string; company: string; source: string; vars: Record<string, string> }) =>
    request<{ ok: true; leadId: string }>("POST", "/public/leads", l),
};

export type LeadRow = Lead & { completed: boolean; reached: number; totalSteps: number };

/** Read natural dimensions before upload so hotspots can be stored as percentages. */
export function imageSize(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { resolve({ width: img.naturalWidth, height: img.naturalHeight }); URL.revokeObjectURL(url); };
    img.onerror = () => { reject(new Error(`${file.name} is not a readable image.`)); URL.revokeObjectURL(url); };
    img.src = url;
  });
}

const VIDEO_MIME = new Set(["video/mp4", "video/quicktime", "video/webm"]);
export const isVideoFile = (f: File) => VIDEO_MIME.has(f.type);

/** Same idea as imageSize, but for video: also returns duration for the record. */
export function videoMeta(file: File): Promise<{ width: number; height: number; durationMs: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => {
      resolve({ width: v.videoWidth, height: v.videoHeight, durationMs: Math.round((v.duration || 0) * 1000) });
      URL.revokeObjectURL(url);
    };
    v.onerror = () => { reject(new Error(`${file.name} is not a readable video.`)); URL.revokeObjectURL(url); };
    v.src = url;
  });
}

export async function uploadMediaFile(file: File) {
  if (isVideoFile(file)) {
    const { width, height, durationMs } = await videoMeta(file);
    if (!width || !height) throw new Error(`Couldn't read ${file.name}'s dimensions. Try a different export of the video.`);
    return api.uploadImage(file, width, height, "video", durationMs);
  }
  const { width, height } = await imageSize(file);
  return api.uploadImage(file, width, height, "image");
}

/** @deprecated use uploadMediaFile, which also accepts video */
export async function uploadImageFile(file: File) {
  const { width, height } = await imageSize(file);
  return api.uploadImage(file, width, height, "image");
}

export const newId = () => crypto.randomUUID();
